import { ChangeDetectionStrategy, Component, HostBinding } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { UpgradeNavigationServiceHandler } from '../../../Common/Routing/upgrade-navigation-handler.service';
import { AnchorSREF } from '../../../UI-Lib/Directives/Anchor-Route/anchorRoute.directive';

export interface AgGridLinkCellRendererParams extends ICellRendererParams {
    getLink?: (params: AgGridLinkCellRendererParams) => string;
    getSref?: (params: AgGridLinkCellRendererParams) => AnchorSREF;
    getLinkAsync?: (params: AgGridLinkCellRendererParams) => Promise<{ stateName: string; params: any }>;
    getButtonLink?: (params: AgGridLinkCellRendererParams) => string;
    onClick?: (params: AgGridLinkCellRendererParams) => void;
    onClickAsync?: (params: AgGridLinkCellRendererParams) => Promise<void>;
    getText?: (params: AgGridLinkCellRendererParams) => string;
    getIcon?: (params: AgGridLinkCellRendererParams) => string;
    isDisabled?: (params: AgGridLinkCellRendererParams) => boolean;
    getHelpContentId?: (params: AgGridLinkCellRendererParams) => string;
    newWindow?: boolean;
    isHelpDisabled?: boolean;
    getStyle?: (params: AgGridLinkCellRendererParams) => object;
}

@Component({
    selector: 'ag-grid-link-cell',
    template: `
        @if (showSrefLink) {
            <a
                class="grid-link-cell"
                anchor-route
                [sref]="sref"
                [isTargetBlank]="params.newWindow"
                helpTooltip
                [helpDisabled]="params.isHelpDisabled"
                [helpContentId]="params.getHelpContentId && params.getHelpContentId(params)"
                position="bottom"
                (click)="onClick()">
                @if (icon) {
                    <i class="fa" [ngClass]="icon"></i>
                }
                {{ text }}
            </a>
        }
        @if (showLink) {
            <a
                class="grid-link-cell"
                (click)="onClick()"
                [ngClass]="{ 'grid-cell-readonly': isDisabled }"
                [href]="link"
                [attr.target]="params.newWindow ? '_blank' : null"
                rel="noopener noreferrer"
                helpTooltip
                [helpDisabled]="params.isHelpDisabled"
                [helpContentId]="params.getHelpContentId && params.getHelpContentId(params)"
                position="bottom"
            >
                @if (icon) {
                    <i class="fa" [ngClass]="icon"></i>
                }
                {{ text }}
            </a>
        }
        @if (showAsyncLink) {
            <span class="anchor-style" (click)="onAsyncClick()">
                @if (icon) {
                    <i class="fa" [ngClass]="icon"></i>
                }
                {{ text }}
            </span>
        }
        @if (showButtonLink) {
            <span class="anchor-style" (click)="onClick()">
                {{ params.getButtonLink(params) }}
            </span>
        }
        @if ((!showSrefLink && !showLink && !showAsyncLink && !showButtonLink) || isDisabled) {
            <span
                class="grid-link-cell"
                [ngClass]="{ 'grid-cell-readonly': isDisabled, 'anchor-style': !isDisabled }"
                (click)="onClick()"
                helpTooltip
                [helpDisabled]="params.isHelpDisabled"
                [helpContentId]="params.getHelpContentId && params.getHelpContentId(params)"
                position="bottom"
            >
                @if (icon) {
                    <i class="fa" [ngClass]="icon"></i>
                }
                {{ text }}
            </span>
        }
    `,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgGridLinkCellRendererComponent implements ICellRendererAngularComp {
    constructor(private _navigationService: UpgradeNavigationServiceHandler) {}

    @HostBinding('style') hostStyle;
    params: AgGridLinkCellRendererParams;

    icon: string;
    sref: AnchorSREF;
    private _link: string;

    get link(): string {
        return this._link || (this.params.getLink && this.params.getLink(this.params));
    }

    get text(): SafeHtml {
        let text: string;
        if (this.params.getText) {
            text = this.params.getText(this.params);
        } else if (this.params.valueFormatted) {
            text = this.params.valueFormatted;
        } else {
            text = this.params.value;
        }

        return text || '';
    }

    get showSrefLink(): boolean {
        if (this.isDisabled) {
            return false;
        }

        return !!this.sref;
    }

    get showLink(): boolean {
        if (this.isDisabled || this.showSrefLink) {
            return false;
        }

        return this.params.getLink && !!this.link;
    }

    get showAsyncLink(): boolean {
        if (this.isDisabled) {
            return false;
        }

        return !!this.params.getLinkAsync;
    }

    get showButtonLink(): boolean {
        if (this.isDisabled) {
            return false;
        }

        return !!this.params.getButtonLink;
    }

    get isDisabled(): boolean {
        return this.params.isDisabled && this.params.isDisabled(this.params);
    }

    agInit(params: AgGridLinkCellRendererParams): void {
        this.params = params;
        this._link = this.params.getLink && this.params.getLink(this.params);
        this.sref = this.params.getSref && this.params.getSref(this.params);
        this.icon = this.params.getIcon && this.params.getIcon(this.params);

        this.hostStyle = this.params && this.params.getStyle && this.params.getStyle(params);
    }

    refresh(): boolean {
        return false; // tells grid to handle refreshing
    }

    onClick(): void {
        if (this.isDisabled) {
            return;
        }

        if (this.params.onClick) {
            this.params.onClick(this.params);
        } else if (this.params.onClickAsync) {
            this.params.onClickAsync(this.params);
        }
    }

    async onAsyncClick(): Promise<void> {
        const nav = await this.params.getLinkAsync(this.params);
        if (!nav) {
            return;
        }

        this._navigationService.go(nav.stateName, nav.params);
        this.onClick();
    }
}
